/**
 * Browser script to diagnose consequence message flow
 *
 * USAGE (3-step pattern from BROWSER_SCRIPTS_GUIDE.md):
 * 1. This script is created
 * 2. Register it in App.jsx
 * 3. Run in browser console:
 *    - window.diagnoseConsequenceMessages()              // Auto-detects schoolId
 *    - window.diagnoseConsequenceMessages('schoolId123') // Explicit schoolId
 */

import { collection, query, where, orderBy, limit, getDocs, collectionGroup, Timestamp } from 'firebase/firestore';
import { db } from '../firebase.js';

async function diagnoseConsequenceMessages(providedSchoolId = null) {
  console.log('\n🔍 === CONSEQUENCE MESSAGE DIAGNOSTICS ===\n');

  try {
    // Get schoolId from parameter or context
    let schoolId = providedSchoolId;

    if (!schoolId) {
      const contextData = localStorage.getItem('swan_school_context');
      if (!contextData) {
        console.error('❌ No school context found. Make sure you\'re logged in.');
        console.error('💡 Or provide schoolId explicitly: diagnoseConsequenceMessages("your-school-id")');
        return;
      }

      const context = JSON.parse(contextData);
      schoolId = context.schoolId;

      if (!schoolId) {
        console.error('❌ No schoolId in context.');
        console.error('💡 Provide schoolId explicitly: diagnoseConsequenceMessages("your-school-id")');
        return;
      }
    }

    console.log(`📚 School ID: ${schoolId}\n`);

    // 1. Check recent consequences
    console.log('1️⃣ Checking recent consequences in disciplineQueue...');
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    const consequencesSnapshot = await getDocs(query(
      collection(db, 'schools', schoolId, 'disciplineQueue'),
      where('issuedAt', '>=', Timestamp.fromDate(yesterday)),
      orderBy('issuedAt', 'desc'),
      limit(10)
    ));

    if (consequencesSnapshot.empty) {
      console.log('⚠️  No consequences found in last 24 hours');
      return;
    }

    console.log(`✅ Found ${consequencesSnapshot.size} consequence(s)\n`);

    for (const doc of consequencesSnapshot.docs) {
      const data = doc.data();
      console.log(`\n--- Consequence: ${doc.id} ---`);
      console.log(`Student: ${data.studentName || data.studentId}`);
      console.log(`Type: ${data.type}`);
      console.log(`Type ID: ${data.typeId || 'N/A'}`);
      console.log(`Issued at: ${data.issuedAt?.toDate?.()}`);
      console.log(`Issued by: ${data.issuedBy || data.teacherId || data.createdBy}`);
      console.log(`Auto-generated: ${data.autoGenerated || false}`);
      console.log(`Sequential #: ${data.sequentialNumber || 'N/A'}`);

      // Check for verified parent
      console.log('\n  👨‍👩‍👧 Checking parent links...');
      const parentLinksSnapshot = await getDocs(query(
        collection(db, 'parentChildLinks'),
        where('studentId', '==', data.studentId),
        where('schoolId', '==', schoolId),
        where('status', '==', 'active')
      ));

      if (parentLinksSnapshot.empty) {
        console.log('  ❌ No active parent links found');
        continue;
      }

      const verifiedLinks = parentLinksSnapshot.docs.filter(doc => {
        const linkData = doc.data();
        return linkData.telegram && linkData.telegram.verified === true;
      });

      if (verifiedLinks.length === 0) {
        console.log(`  ⚠️  Found ${parentLinksSnapshot.size} parent link(s) but NONE verified for Telegram`);
        parentLinksSnapshot.docs.forEach(pDoc => {
          const linkData = pDoc.data();
          console.log(`     - Parent: ${linkData.parentId}, Telegram verified: ${linkData.telegram?.verified || false}`);
        });
        continue;
      }

      console.log(`  ✅ Found ${verifiedLinks.length} verified parent link(s)`);
      const parentLink = verifiedLinks[0].data();
      console.log(`     Parent ID: ${parentLink.parentId}`);
      console.log(`     Telegram chat: ${parentLink.telegram?.chatId}`);

      // Check for messages
      console.log('\n  📨 Checking for messages...');
      const messagesSnapshot = await getDocs(query(
        collectionGroup(db, 'messages'),
        where('metadata.consequenceId', '==', doc.id),
        limit(5)
      ));

      if (messagesSnapshot.empty) {
        console.log('  ❌ NO MESSAGES FOUND FOR THIS CONSEQUENCE!');
        console.log('  ⚠️  This is the problem - the trigger should have created a message');
        console.log('\n  🔍 Possible causes:');
        console.log('     1. Trigger not deployed: firebase deploy --only functions:onConsequenceCreated');
        console.log('     2. Trigger error (check Cloud Functions logs)');
        console.log('     3. Consequence created before trigger was deployed');
        console.log('     4. Consequence missing required fields (schoolId, studentId, issuedBy)');
      } else {
        console.log(`  ✅ Found ${messagesSnapshot.size} message(s):`);
        messagesSnapshot.docs.forEach(msgDoc => {
          const msgData = msgDoc.data();
          console.log(`\n     Message ${msgDoc.id}:`);
          console.log(`     Status: ${msgData.status}`);
          console.log(`     Type: ${msgData.type}`);
          console.log(`     Priority: ${msgData.priority}`);
          console.log(`     Created: ${msgData.createdAt?.toDate?.() || 'N/A'}`);
          console.log(`     Scheduled for: ${msgData.scheduledFor?.toDate?.() || 'Immediate'}`);
          console.log(`     Telegram sent: ${msgData.delivery?.telegram?.sentAt?.toDate?.() || 'Not sent'}`);
          if (msgData.delivery?.telegram?.error) {
            console.log(`     ❌ Telegram error: ${msgData.delivery.telegram.error}`);
          }
          if (msgData.delivery?.telegram?.messageId) {
            console.log(`     Telegram message ID: ${msgData.delivery.telegram.messageId}`);
          }
        });
      }
    }

    // 2. Check message queue status
    console.log('\n\n2️⃣ Checking message queue...');
    const queuedSnapshot = await getDocs(query(
      collectionGroup(db, 'messages'),
      where('schoolId', '==', schoolId),
      where('status', '==', 'queued'),
      limit(10)
    ));

    if (queuedSnapshot.empty) {
      console.log('   No queued messages (all sent or none created)');
    } else {
      console.log(`   ⏳ Found ${queuedSnapshot.size} queued message(s):`);
      queuedSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const scheduledInfo = data.scheduledFor
          ? ` (scheduled for ${data.scheduledFor.toDate()})`
          : ' (immediate)';
        console.log(`   - ${doc.id}: ${data.type} for parent ${data.parentId}${scheduledInfo}`);
        console.log(`     Created: ${data.createdAt?.toDate?.()}`);
      });
      console.log('\n   ℹ️  Message worker runs every 2 minutes');
      console.log('   ℹ️  Queued messages should be sent within 2-4 minutes');
    }

    // 3. Summary
    console.log('\n\n📊 SUMMARY:');
    console.log(`   Consequences found: ${consequencesSnapshot.size}`);
    console.log(`   Queued messages: ${queuedSnapshot.size}`);
    console.log('\n   Next steps if messages are missing:');
    console.log('   1. Check Cloud Functions logs in Firebase Console');
    console.log('   2. Verify trigger is deployed: firebase deploy --only functions:onConsequenceCreated');
    console.log('   3. Create a test consequence and watch for message creation');

  } catch (error) {
    console.error('❌ Error during diagnosis:', error);
    console.error('Stack:', error.stack);
  }

  console.log('\n=== END DIAGNOSTICS ===\n');
}

export default diagnoseConsequenceMessages;